import { ENROLLMENT_STATE_LABELS, canChangeEnrollmentState } from '@repo/types';
import { useQueryClient } from '@tanstack/react-query';
import { COURSE_STATE, ENROLLMENT_STATE } from '@theailab-dev/parchment';
import type { EnrollmentState } from '@theailab-dev/parchment';
import React, { useState } from 'react';
import { HiPencil } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import { changeEnrollmentState } from '../../../api/enrollmentApi';
import { DetailPageLayout } from '../../../components/Layout/DetailPageLayout';
import { Card, Badge, Button, InfoItem } from '../../../components/ui';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { useEnrollmentDetail } from '../../../hooks/useEnrollmentDetail';
import { getEnrollmentStateBadgeVariant } from '../utils/getEnrollmentStateBadgeVariant';

/**
 * EnrollmentDetail 컴포넌트 - 수강 상세 정보 페이지
 */
const EnrollmentDetail: React.FC = () => {
  const navigate = useNavigate();

  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const queryClient = useQueryClient();
  const id = enrollmentId ? parseInt(enrollmentId, 10) : 0;

  const { data: enrollment, isLoading, isError, error } = useEnrollmentDetail(id);

  // 학습자 정보 페이지로 이동
  const handleMoveToAccountDetailPage = () => {
    //dashboard/users/
    navigate(`/dashboard/users/${enrollment?.account.id}`);
  };

  // 상태 변경 관련 state
  const [isChangingState, setIsChangingState] = useState(false);
  const [showChangeStateModal, setShowChangeStateModal] = useState(false);
  const [selectedState, setSelectedState] = useState<EnrollmentState | ''>('');
  const [showResultModal, setShowResultModal] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  /**
   * 상태 변경 모달 열기
   */
  const handleChangeStateClick = () => {
    if (enrollment?.state) {
      setSelectedState(enrollment.state);
    }
    setShowChangeStateModal(true);
  };

  /**
   * 상태 변경 실행
   */
  const handleConfirmChangeState = async () => {
    if (!enrollment || !selectedState) {
      setShowChangeStateModal(false);
      return;
    }

    // 변경 사항이 없는 경우 안내
    if (selectedState === enrollment.state) {
      setShowResultModal({
        type: 'info',
        message: '변경된 내용이 없습니다.',
      });
      setShowChangeStateModal(false);
      return;
    }

    try {
      setIsChangingState(true);
      await changeEnrollmentState(id, selectedState);

      // 성공 모달 표시
      setShowResultModal({
        type: 'success',
        message: `수강 상태가 ${ENROLLMENT_STATE_LABELS[selectedState]}(으)로 변경되었습니다.`,
      });

      // 데이터 재조회
      await queryClient.invalidateQueries({
        queryKey: ['enrollment', id],
      });

      setShowChangeStateModal(false);
      setSelectedState('');
    } catch (error) {
      console.error('상태 변경 실패:', error);

      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';

      // 에러 모달 표시
      setShowResultModal({
        type: 'error',
        message: errorMessage,
      });

      setShowChangeStateModal(false);
      setSelectedState('');
    } finally {
      setIsChangingState(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 시간 포맷팅 (HH:MM:SS)
  const formatDuration = (duration: string | null) => {
    if (!duration) return '-';
    return duration;
  };

  // 진도율 계산
  const progress =
    enrollment && enrollment.totalLessonCount > 0
      ? Math.round((enrollment.completedLessonCount / enrollment.totalLessonCount) * 100)
      : 0;

  return (
    <DetailPageLayout
      title="수강 상세 정보"
      isLoading={isLoading}
      isError={isError}
      errorMessage={error instanceof Error ? error.message : '수강 정보를 불러오는 중 오류가 발생했습니다.'}
      badges={
        enrollment ? (
          <Badge variant={getEnrollmentStateBadgeVariant(enrollment.state)}>
            {ENROLLMENT_STATE_LABELS[enrollment.state] || enrollment.state}
          </Badge>
        ) : undefined
      }>
      {enrollment && (
        <>
          {/* 상태 변경 확인 모달 */}
          <ConfirmModal
            isOpen={showChangeStateModal}
            onClose={() => {
              setShowChangeStateModal(false);
              setSelectedState('');
            }}
            type="info"
            title="수강 상태 변경"
            message={
              <div className="space-y-4">
                <p className="text-base text-slate-600">수강 상태를 변경하시겠습니까?</p>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">새 상태 선택</label>
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value as EnrollmentState)}
                    disabled={isChangingState}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-base text-slate-800 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100">
                    {Object.values(ENROLLMENT_STATE)
                      .filter((state) => state !== ENROLLMENT_STATE.COMPLETED)
                      .map((state) => (
                        <option key={state} value={state}>
                          {ENROLLMENT_STATE_LABELS[state]}
                        </option>
                      ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    현재 상태: <strong>{enrollment?.state && ENROLLMENT_STATE_LABELS[enrollment.state]}</strong> → 변경
                    후: <strong>{selectedState && ENROLLMENT_STATE_LABELS[selectedState]}</strong>
                  </p>
                </div>
              </div>
            }
            buttons={[
              {
                label: '취소',
                onClick: () => {
                  setShowChangeStateModal(false);
                  setSelectedState('');
                },
                variant: 'secondary',
                disabled: isChangingState,
              },
              {
                label: '변경',
                onClick: handleConfirmChangeState,
                variant: 'primary',
                isLoading: isChangingState,
              },
            ]}
          />

          {/* 결과 모달 */}
          {showResultModal && (
            <ConfirmModal
              isOpen={true}
              onClose={() => setShowResultModal(null)}
              type={showResultModal.type}
              title={
                showResultModal.type === 'success'
                  ? '변경 완료'
                  : showResultModal.type === 'error'
                    ? '변경 실패'
                    : '알림'
              }
              message={showResultModal.message}
              buttons={[
                {
                  label: '확인',
                  onClick: () => setShowResultModal(null),
                  variant: 'primary',
                },
              ]}
            />
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* 코스 정보 */}
            <Card title="코스 정보">
              <div className="space-y-4">
                <div className="border-b border-slate-100 pb-4">
                  <h4 className="mb-1 text-xl font-bold text-slate-800">{enrollment.course.title}</h4>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <InfoItem label="코스 ID" value={enrollment.course.id} />
                  <InfoItem
                    label="상태"
                    value={
                      <Badge variant={enrollment.course.state === COURSE_STATE.NORMAL ? 'success' : 'warning'}>
                        {enrollment.course.state}
                      </Badge>
                    }
                  />
                  <InfoItem label="유형" value={enrollment.course.kind} />
                  <InfoItem label="난이도" value={enrollment.course.level || '-'} />
                  <InfoItem label="전체 강의 수" value={`${enrollment.course.totalLessonCount}개`} />
                  <InfoItem label="총 학습 시간" value={formatDuration(enrollment.course.totalDuration)} />
                </div>
              </div>
            </Card>

            {/* 학습자 정보 */}
            <Card
              title="학습자 정보"
              actions={
                <Button size="sm" variant="outline" onClick={handleMoveToAccountDetailPage} leftIcon={<HiPencil />}>
                  학습자 상세로 이동
                </Button>
              }>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoItem label="ID" value={enrollment.account.id} />
                <InfoItem label="이름" value={enrollment.account.name} />
                <InfoItem label="이메일" value={enrollment.account.email} />
                <InfoItem label="전화번호" value={enrollment.account.phone ?? '-'} />
              </div>
            </Card>

            {/* 수강 정보 */}
            <Card
              title="수강 정보"
              actions={
                canChangeEnrollmentState(enrollment.state) && (
                  <Button size="sm" variant="outline" onClick={handleChangeStateClick} leftIcon={<HiPencil />}>
                    상태 변경
                  </Button>
                )
              }>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoItem label="수강 ID" value={enrollment.id} />
                <InfoItem
                  label="상태"
                  value={
                    <Badge variant={getEnrollmentStateBadgeVariant(enrollment.state)}>
                      {ENROLLMENT_STATE_LABELS[enrollment.state]}
                    </Badge>
                  }
                />
                <InfoItem label="유형" value={enrollment.kind} />
                <InfoItem label="등록일" value={formatDate(enrollment.enrolledAt)} />
                <InfoItem label="완료일" value={formatDate(enrollment.completedAt)} />
                <InfoItem label="만료일" value={formatDate(enrollment.expireAt)} />
                <InfoItem label="주문서 ID" value={enrollment.orderSheetId || '-'} />
                <InfoItem label="수료증 발급" value={enrollment.isCertificateIssued ? '발급됨' : '미발급'} />
              </div>
            </Card>

            {/* 학습 진도 */}
            <Card title="학습 진도">
              <div className="space-y-6">
                {/* 진행률 바 */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">전체 진도율</span>
                    <span className="text-lg font-bold text-indigo-600">{progress}%</span>
                  </div>
                  <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-3 rounded-full bg-indigo-500 transition-all duration-500"
                      style={{ width: `${progress}%` }}></div>
                  </div>
                  <p className="mt-2 text-right text-xs text-slate-500">
                    {enrollment.completedLessonCount} / {enrollment.totalLessonCount} 강의 완료
                  </p>
                </div>

                {/* 학습 상세 정보 */}
                <div className="grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
                  <InfoItem label="완료한 강의 수" value={`${enrollment.completedLessonCount}개`} />
                  <InfoItem label="전체 강의 수" value={`${enrollment.totalLessonCount}개`} />
                  <InfoItem label="마지막 학습일" value={formatDate(enrollment.lastAttendedAt)} />
                  <InfoItem label="마지막 학습 강의 ID" value={enrollment.lastLessonId || '-'} />
                </div>

                {/* 학습자료 다운로드 여부 */}
                {enrollment.course.materialsUrl && (
                  <div className="border-t border-slate-100 pt-4">
                    <h4 className="mb-3 text-sm font-semibold text-slate-700">전체 학습자료 다운로드 여부</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                        <span className="text-sm font-medium text-slate-700">{enrollment.course.title}</span>
                        <Badge variant={enrollment.isMaterialsDownloaded ? 'success' : 'neutral'}>
                          {enrollment.isMaterialsDownloaded ? '다운완료' : '미다운'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </DetailPageLayout>
  );
};

export default EnrollmentDetail;
